import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    grid-gap: 25px;
    position: relative;
    margin-top: 50px;
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    background-color: var(--light-navy);
    border-radius: var(--border-radius);
    padding: 2rem;
    transition: var(--transition);
  }

  .project-title {
    margin-bottom: 15px;
    color: var(--lightest-slate);
    font-size: var(--fz-xxl);

    a {
      color: var(--green);
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .project-authors {
    color: var(--light-slate);
    font-size: var(--fz-md);
    line-height: 1.5;
  }

  .project-meta {
    color: var(--slate);
    font-size: var(--fz-sm);
    margin-top: 10px;
  }
`;

const StyledProject = styled.li`
  position: relative;
`;

const Projects = () => {
  const revealTitle = useRef(null);
  const revealPaper = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealPaper.current, srConfig());
  }, []);

  const paper = {
    title: 'FGPB-EMR: Fine-grained privacy blockchain for electronic medical record sharing',
    authors: [
      'Wei Liang',
      'Yang Yang',
      'Sisi Zhou',
      'Zhishun Zhang',
      'Yuxiang Chen',
      'Kai Jin',
      'Xiong Li',
      'Kuanching Li',
      'Jiannong Cao',
    ],
    link:
      'https://www.researchgate.net/publication/396792648_FGPB-EMR_Fine-grained_privacy_blockchain_for_electronic_medical_record_sharing',
    meta: [
      'Hunan University of Science and Technology, Hunan University, Hebei University, UESTC, Xidian University, PolyU',
      'Received 26 July 2024, Revised 28 May 2025, Accepted 16 October 2025, Online 22 October 2025',
    ],
  };

  return (
    <StyledProjectsSection>
      <h2 ref={revealTitle}>Published Papers</h2>

      <ul className="projects-grid">
        <TransitionGroup component={null}>
          <CSSTransition classNames="fadeup" timeout={300} exit={false}>
            <StyledProject ref={revealPaper}>
              <div className="project-inner">
                <h3 className="project-title">
                  <a href={paper.link} target="_blank" rel="noreferrer">
                    {paper.title}
                  </a>
                </h3>
                <div className="project-authors">
                  <strong>Authors:</strong> {paper.authors.join(', ')}
                </div>
                <div className="project-meta">
                  {paper.meta.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            </StyledProject>
          </CSSTransition>
        </TransitionGroup>
      </ul>
    </StyledProjectsSection>
  );
};

export default Projects;
